import { useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties, MouseEvent } from "react";
import type { CardView, Keyword } from "../api/types";
import type { Theme } from "../theme/pixel";
import { factionFor } from "../theme/factions";
import { rarityFor } from "../theme/rarities";
import { E32 } from "../theme/endesga32";
import { worldLabel } from "../theme/factions";
import { CardFoil } from "./CardFoil";
import { KeywordTooltip } from "./KeywordTooltip";
import { Sigil } from "./Sigil";
import { ORNAMENT_SIGIL } from "../theme/factions";

export type CardSize = "mini" | "small" | "normal" | "hero";

// "Creature — Human Knight" / "Creature — Goblin" / "Creature — Knight" /
// "Creature" / "Spell". Race and Class are creature-only and surface here
// in MtG-style "<Type> — <Race> <Class>" form. Either may be absent.
// Both fields come from the engine's closed subtypes.json enums, lowercased,
// so we capitalize for display here. The em-dash is the visual separator
// between card type and subtype line (same convention as MtG).
function formatTypeLine(card: CardView): string {
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  const parts: string[] = [];
  if (card.race) parts.push(cap(card.race));
  if (card.class) parts.push(cap(card.class));
  if (parts.length === 0) return card.type;
  return `${card.type} — ${parts.join(" ")}`;
}

interface VariantSpec {
  /** Pixel width when nothing constrains the card. */
  naturalWidthPx: number;
  showStats: boolean;
  showRulesText: boolean;
  showFlavorText: boolean;
  showWorldFooter: boolean;
  // All values below are cqw (% of card width).
  pad: number;
  costGem: number;
  costGemFont: number;
  /** Already factors in the 0.72× display-font shrink. */
  nameSize: number;
  /** Larger name slot for summon_only troops (no rules text crowds it). */
  troopNameSize: number;
  /** Stats line on summon_only troops, sized so a mini troop reads at the
   *  same px next to a small creature when both are at natural width. */
  troopStatsSize: number;
  bodySize: number;
  statsFont: number;
  typeLineFont: number;
  smallGap: number;
  rulesGap: number;
  rulesBottomInset: number;
  worldFooterBottom: number;
  worldFont: number;
  worldSigilSize: number;
  topRightSigilSize: number;
  artPlaceholderSigil: number;
  costGemBorderPx: number;
  faceDownTitleSize: number;
  faceDownInfW: number;
  faceDownInfH: number;
  faceDownStackGap: number;
  faceDownFrameInset: number;
}

const VARIANTS: Record<CardSize, VariantSpec> = {
  mini: {
    naturalWidthPx: 96,
    showStats: true,
    showRulesText: false,
    showFlavorText: false,
    showWorldFooter: false,
    pad: 6.25,
    costGem: 20.8333,
    costGemFont: 7.9167,
    nameSize: 7.5,
    troopNameSize: 9.8958,
    troopStatsSize: 10.4167,
    bodySize: 8.3333,
    statsFont: 9.375,
    typeLineFont: 8.3333,
    smallGap: 6.25,
    rulesGap: 8.3333,
    rulesBottomInset: 20.8333,
    worldFooterBottom: 3.125,
    worldFont: 7.2917,
    worldSigilSize: 8.3333,
    topRightSigilSize: 14.5833,
    artPlaceholderSigil: 22.9167,
    costGemBorderPx: 2,
    faceDownTitleSize: 7.2917,
    faceDownInfW: 60,
    faceDownInfH: 30,
    faceDownStackGap: 6.875,
    faceDownFrameInset: 4.1667,
  },
  small: {
    naturalWidthPx: 128,
    showStats: true,
    showRulesText: false,
    showFlavorText: false,
    showWorldFooter: false,
    pad: 6.25,
    costGem: 18.75,
    costGemFont: 7.125,
    nameSize: 6.1875,
    troopNameSize: 8.1641,
    troopStatsSize: 7.8125,
    bodySize: 7.0313,
    statsFont: 7.8125,
    typeLineFont: 6.25,
    smallGap: 4.6875,
    rulesGap: 6.25,
    rulesBottomInset: 17.1875,
    worldFooterBottom: 2.3438,
    worldFont: 5.4688,
    worldSigilSize: 6.25,
    topRightSigilSize: 13.2813,
    artPlaceholderSigil: 25,
    costGemBorderPx: 2,
    faceDownTitleSize: 6.5625,
    faceDownInfW: 60,
    faceDownInfH: 30,
    faceDownStackGap: 7.0313,
    faceDownFrameInset: 4.6875,
  },
  normal: {
    naturalWidthPx: 180,
    showStats: true,
    showRulesText: true,
    showFlavorText: false,
    showWorldFooter: true,
    pad: 5.5556,
    costGem: 16.6667,
    costGemFont: 6.3333,
    nameSize: 5.2,
    troopNameSize: 6.8611,
    troopStatsSize: 5.5556,
    bodySize: 6.6667,
    statsFont: 7.2222,
    typeLineFont: 5.6667,
    smallGap: 3.3333,
    rulesGap: 4.4444,
    rulesBottomInset: 13.3333,
    worldFooterBottom: 1.6667,
    worldFont: 4.8,
    worldSigilSize: 5.5556,
    topRightSigilSize: 11.6667,
    artPlaceholderSigil: 17.7778,
    costGemBorderPx: 3,
    faceDownTitleSize: 6.5,
    faceDownInfW: 60,
    faceDownInfH: 30,
    faceDownStackGap: 7.2222,
    faceDownFrameInset: 4.4444,
  },
  hero: {
    naturalWidthPx: 300,
    showStats: true,
    showRulesText: true,
    showFlavorText: true,
    showWorldFooter: true,
    pad: 5.3333,
    costGem: 15.3333,
    costGemFont: 5.8267,
    nameSize: 4.56,
    troopNameSize: 6.0167,
    troopStatsSize: 3.3333,
    bodySize: 4.6667,
    statsFont: 5,
    typeLineFont: 3.9667,
    smallGap: 2,
    rulesGap: 2.6667,
    rulesBottomInset: 10,
    worldFooterBottom: 1,
    worldFont: 3.36,
    worldSigilSize: 4,
    topRightSigilSize: 10.6667,
    artPlaceholderSigil: 16,
    costGemBorderPx: 3,
    faceDownTitleSize: 6.5,
    faceDownInfW: 60,
    faceDownInfH: 30,
    faceDownStackGap: 7,
    faceDownFrameInset: 4.6667,
  },
};

export function naturalCardWidthPx(size: CardSize): number {
  return VARIANTS[size].naturalWidthPx;
}

const KEYWORD_LIST: Keyword[] = [
  "ward",
  "rush",
  "volatile",
  "rage",
  "rooted",
  "sneaky",
  "grow",
  "deathtouch",
  "indestructible",
  "frenzy",
  "reckless",
  "sealed",
  "shielded",
  "reinforce",
  "runebound",
  "rally",
  "intimidating",
  "manabloom",
  "rampaging",
  "spellproof",
  "trapproof",
  "magicproof",
];

// Bold these together with their trailing integer ("Volatile 5", "Grow 5").
const PARAMETERIZED_KEYWORDS: Keyword[] = [
  "volatile",
  "sealed",
  "manabloom",
  "rally",
  "grow",
  "rage",
  "frenzy",
  "reinforce",
  "runebound",
];

// X-actions only match when followed by an integer, so the bare verb
// in mid-sentence prose ("...Mill the field...") is not bolded.
const X_ACTIONS: readonly string[] = ["raise", "mill"];

function renderRules(
  text: string | null | undefined,
  opts?: { theme: Theme },
) {
  if (!text) return null;
  const plain = KEYWORD_LIST.filter((k) => !PARAMETERIZED_KEYWORDS.includes(k));
  const paramPart = `(?:${PARAMETERIZED_KEYWORDS.join("|")})(?:\\s+\\d+)?`;
  const plainPart = plain.join("|");
  const xActionPart = `(?:${X_ACTIONS.join("|")})\\s+\\d+`;
  const re = new RegExp(`\\b(${xActionPart}|${paramPart}|${plainPart})\\b`, "gi");
  return text.split("\n").map((line, li) => {
    const parts: Array<string | JSX.Element> = [];
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(line)) !== null) {
      if (m.index > last) parts.push(line.slice(last, m.index));
      const matched = m[0];
      const key = `${li}-${m.index}`;
      const bold = (
        <strong key={key} style={{ fontWeight: 700 }}>
          {matched}
        </strong>
      );
      parts.push(
        opts ? (
          <KeywordTooltip key={key} keyword={matched} theme={opts.theme}>
            {bold}
          </KeywordTooltip>
        ) : (
          bold
        ),
      );
      last = m.index + matched.length;
    }
    if (last < line.length) parts.push(line.slice(last));
    return <div key={li}>{parts}</div>;
  });
}

// `lineHeight: 1.25` because Press Start 2P glyphs sit flush against the
// top of the em-box; tighter leading clips capital letters when overflow
// is hidden.
function FitText({
  text,
  maxSize,
  maxLines = 2,
  lineHeight = 1.25,
  style,
}: {
  text: string;
  maxSize: number;
  maxLines?: number;
  lineHeight?: number;
  style?: CSSProperties;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState(maxSize);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const parent = el.parentElement;
    const fit = () => {
      let s = maxSize;
      const apply = (cqw: number) => {
        el.style.fontSize = `${cqw}cqw`;
        el.style.maxHeight = `${cqw * lineHeight * maxLines}cqw`;
      };
      apply(s);
      while (
        s > 0.5 &&
        (el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight)
      ) {
        s -= 0.25;
        apply(s);
      }
      setSize(s);
    };
    fit();
    if (!parent) return;
    const ro = new ResizeObserver(fit);
    ro.observe(parent);
    return () => ro.disconnect();
  }, [text, maxSize, maxLines, lineHeight]);

  return (
    <div
      ref={ref}
      style={{
        ...style,
        fontSize: `${size}cqw`,
        lineHeight,
        maxHeight: `${size * lineHeight * maxLines}cqw`,
        overflow: "hidden",
        wordBreak: "keep-all",
        overflowWrap: "normal",
        hyphens: "manual",
      }}
    >
      {text}
    </div>
  );
}

function FitBlock({
  children,
  maxSize,
  lineHeight = 1.35,
}: {
  children: React.ReactNode;
  maxSize: number;
  lineHeight?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState(maxSize);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const parent = el.parentElement;
    const fit = () => {
      let s = maxSize;
      el.style.fontSize = `${s}cqw`;
      while (
        s > 0.5 &&
        (el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight)
      ) {
        s -= 0.25;
        el.style.fontSize = `${s}cqw`;
      }
      setSize(s);
    };
    fit();
    if (!parent) return;
    const ro = new ResizeObserver(fit);
    ro.observe(parent);
    return () => ro.disconnect();
  }, [children, maxSize, lineHeight]);

  return (
    <div
      ref={ref}
      style={{
        fontSize: `${size}cqw`,
        lineHeight,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        wordBreak: "keep-all",
        overflowWrap: "normal",
        hyphens: "manual",
      }}
    >
      {children}
    </div>
  );
}

interface CardProps {
  card: CardView;
  size?: CardSize;
  faceDown?: boolean;
  onClick?: (ev: MouseEvent<HTMLDivElement>) => void;
  onHover?: (card: CardView | null) => void;
  onMouseDown?: (ev: MouseEvent<HTMLDivElement>) => void;
  highlight?: boolean;
  dim?: boolean;
  selected?: boolean;
  theme: Theme;
  /** Small overlay pill, e.g. "×2" block count. */
  badge?: string;
  /** Outer ring used by the target picker for candidates / picks. */
  ringColor?: string;
  /** Width of the outer `ringColor` in px. Default 4. Target-picked
   *  cards use a thick (12px) ring so they read as "this is the card
   *  the spell will hit" instead of as a halo on a candidate. */
  ringWidth?: number;
  /** Default green; combat passes orange-red for declared / incoming attackers. */
  selectionColor?: string;
  /** Extra ring drawn outside the `selected` ring (combat state, etc.).
   *  Independent of inspection so an inspected attacker shows both. */
  extraRing?: { color: string; style?: "solid" | "dashed" };
  /** When true, bolded keywords are wrapped in hover tooltips. */
  keywordTooltips?: boolean;
}

export function Card({
  card,
  size = "normal",
  faceDown = false,
  onClick,
  onHover,
  onMouseDown,
  highlight,
  dim,
  selected,
  theme,
  badge,
  ringColor,
  selectionColor = E32.fern,
  extraRing,
  ringWidth,
  keywordTooltips = false,
}: CardProps) {
  const f = factionFor(card.faction);
  const rb = rarityFor(card.rarity);
  const v = VARIANTS[size];

  // When `onClick` is provided the card behaves as a button. Render the
  // same DOM but layer on the keyboard/SR plumbing so a tab user can
  // reach it and Enter/Space trigger the click. The `onKeyDown` is
  // synthesised — Card consumers don't pass their own.
  const interactiveProps = onClick
    ? {
        role: "button" as const,
        tabIndex: 0,
        "aria-label": faceDown
          ? "Face-down card"
          : card.summon_only
            ? `${card.name} (summon)`
            : `${card.name}, ${card.type}`,
        "aria-pressed": selected ? true : undefined,
        onKeyDown: (e: import("react").KeyboardEvent<HTMLDivElement>) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            // Build a minimal MouseEvent-shape for onClick handlers
            // that only care about target/currentTarget.
            onClick(
              e as unknown as import("react").MouseEvent<HTMLDivElement>,
            );
          }
        },
      }
    : {};

  const innerWPct = 100 - 2 * v.pad;
  const artLifect = v.showRulesText ? innerWPct * 0.62 : innerWPct;

  const selRing = 6;
  // Layered outer rings, innermost first:
  //   1. selected (green) → only when the card is shown in the sidebar
  //   2. highlight (hover etc.) → only when not selected
  //   3. extraRing (combat state) → independent of selected, stacks outside it
  //   4. ringColor (target picker) → outermost
  // The green "shown in sidebar" ring is suppressed whenever the card
  // already carries a combat ring — stacking both makes neither
  // readable. The sidebar still shows the card; the missing green
  // just signals "you can already see this card has another role."
  let ringOffset = 0;
  const layers: string[] = [];
  if (selected && !extraRing) {
    layers.push(`0 0 0 ${ringOffset + selRing}px ${selectionColor}`);
    ringOffset += selRing;
  } else if (highlight && !extraRing) {
    layers.push(`0 0 0 ${ringOffset + selRing}px ${selectionColor}`);
    ringOffset += selRing;
  }
  if (extraRing) {
    layers.push(`0 0 0 ${ringOffset + selRing}px ${extraRing.color}`);
    ringOffset += selRing;
  }
  if (ringColor) {
    const w = ringWidth ?? 4;
    layers.push(`0 0 0 ${ringOffset + w}px ${ringColor}`);
  }
  const outerShadows = layers;

  const shapeStyle: CSSProperties = {
    borderRadius: 0,
    border: `${rb.borderWidth}px solid ${rb.color}`,
    background: theme.panelSolid,
    boxShadow: outerShadows.join(", "),
    imageRendering: "pixelated",
  };

  const cardStyle: CSSProperties = {
    position: "relative",
    width: "100%",
    aspectRatio: "5 / 7",
    cursor: onClick ? "pointer" : "default",
    transition: "transform 200ms cubic-bezier(.2,.9,.2,1), box-shadow 200ms, filter 150ms",
    filter: dim ? "grayscale(0.9) brightness(0.88) contrast(0.9)" : "none",
    overflow: "hidden",
    flexShrink: 0,
    fontFamily: theme.fontBody,
    color: theme.ink,
    userSelect: "none",
    WebkitUserSelect: "none",
    // Stacking context for CardFoil's mix-blend layers.
    isolation: "isolate",
    // Required for the cqw internal scaling.
    containerType: "inline-size",
    ...shapeStyle,
  };

  if (faceDown) {
    const backStyle: CSSProperties = {
      ...cardStyle,
      background: theme.panelEdgeStrong,
      border: `${rb.borderWidth}px solid ${theme.panelEdge}`,
    };
    return (
      <div
        data-card-tile="true"
        style={backStyle}
        onClick={onClick}
        onMouseEnter={() => onHover?.(card)}
        onMouseLeave={() => onHover?.(null)}
        {...interactiveProps}
      >
        <div
          style={{
            position: "absolute",
            inset: `${v.faceDownFrameInset}cqw`,
            border: `2px solid ${theme.bgDeep}`,
            boxShadow: `inset 0 0 0 2px ${theme.panelEdgeStrong}, inset 0 0 0 3px ${theme.panelEdge}`,
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: `${v.faceDownStackGap}cqw`,
            fontFamily: theme.fontDisplay,
            color: theme.bgDeep,
            textShadow: `1px 1px 0 ${E32.ink}`,
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: `${v.faceDownTitleSize}cqw`,
              letterSpacing: theme.letterSpacing,
            }}
          >
            DECK OF
          </div>
          <svg
            viewBox="0 0 100 50"
            fill="none"
            stroke={E32.amber}
            strokeWidth={10}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              filter: `drop-shadow(2px 2px 0 ${E32.ink})`,
              width: `${v.faceDownInfW}cqw`,
              height: `${v.faceDownInfH}cqw`,
            }}
          >
            <path d="M 25 25 C 25 5, 50 5, 50 25 C 50 45, 75 45, 75 25 C 75 5, 50 5, 50 25 C 50 45, 25 45, 25 25 Z" />
          </svg>
          <div
            style={{
              fontSize: `${v.faceDownTitleSize}cqw`,
              letterSpacing: theme.letterSpacing,
            }}
          >
            INFINITY
          </div>
        </div>
      </div>
    );
  }

  const washIntensity = rb.key === "legendary" ? "44" : rb.key === "rare" ? "33" : "20";
  const rarityWash = `${rb.color}${washIntensity}`;

  // Summon-only troops are never deckable; the standard chrome (cost,
  // sigil, type label, art well) is dropped in favour of a name + stats
  // layout.
  if (card.summon_only) {
    return (
      <div
        data-card-tile="true"
        style={cardStyle}
        onClick={onClick}
        onMouseEnter={() => onHover?.(card)}
        onMouseLeave={() => onHover?.(null)}
        {...interactiveProps}
      >
        <div
          style={{ position: "absolute", inset: 0, background: rarityWash, pointerEvents: "none" }}
        />
        <CardFoil rarity={card.rarity} />
        {dim && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "repeating-linear-gradient(45deg, rgba(26,15,5,0.18) 0 3px, transparent 3px 8px)",
              pointerEvents: "none",
              zIndex: 4,
            }}
          />
        )}
        <PixelCorner pos="tl" color={rb.color} />
        <PixelCorner pos="tr" color={rb.color} />
        <PixelCorner pos="bl" color={rb.color} />
        <PixelCorner pos="br" color={rb.color} />

        <div
          style={{
            position: "absolute",
            inset: `${v.pad}cqw`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-between",
            gap: `${v.smallGap}cqw`,
          }}
        >
          <FitText
            text={card.name}
            maxSize={v.troopNameSize}
            maxLines={3}
            style={{
              fontFamily: theme.fontDisplay,
              fontWeight: 600,
              color: theme.ink,
              textAlign: "center",
              width: "100%",
            }}
          />

          {card.type === "creature" && (
            <span
              style={{
                fontFamily: theme.fontMono,
                fontSize: `${v.troopStatsSize}cqw`,
                fontWeight: 700,
                color: theme.ink,
                flexShrink: 0,
              }}
            >
              <span style={{ color: E32.rust }}>{card.current_attack}</span>
              <span style={{ color: theme.inkFaint, margin: "0 2px" }}>/</span>
              <span style={{ color: E32.forest }}>{card.remaining_life}</span>
            </span>
          )}

          <div style={{ height: 0 }} />
        </div>

        {badge && (
          <div
            style={{
              position: "absolute",
              top: 6,
              right: 6,
              padding: "2px 6px",
              background: theme.panelEdgeStrong,
              color: theme.panelSolid,
              fontFamily: theme.fontDisplay,
              fontSize: 9,
              border: `2px solid ${theme.bgDeep}`,
              zIndex: 5,
            }}
          >
            {badge}
          </div>
        )}
      </div>
    );
  }

  const stats = card.type === "creature" ? (
    <span
      style={{
        fontFamily: theme.fontMono,
        fontSize: `${v.statsFont}cqw`,
        fontWeight: 700,
        color: theme.ink,
        flexShrink: 0,
        marginLeft: 4,
      }}
    >
      <span style={{ color: E32.rust }}>{card.current_attack}</span>
      <span style={{ color: theme.inkFaint, margin: "0 2px" }}>/</span>
      <span style={{ color: E32.forest }}>{card.remaining_life}</span>
    </span>
  ) : null;

  const artContent = card.image_url ? (
    <img
      src={card.image_url}
      alt={card.name}
      loading="lazy"
      draggable={false}
      onDragStart={(e) => e.preventDefault()}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        imageRendering: "pixelated",
        // Disable native image drag so it doesn't hijack the
        // hand-to-board / block-line drag gestures.
        WebkitUserDrag: "none",
        userSelect: "none",
        WebkitUserSelect: "none",
        pointerEvents: "none",
      } as React.CSSProperties}
    />
  ) : (
    <Sigil
      faction={f.key}
      size={`${v.artPlaceholderSigil}cqw`}
      color={f.color}
      style={{ opacity: 0.6 }}
    />
  );

  const artTop = v.pad + v.costGem + v.smallGap;
  const typeLineTop = artTop + artLifect + v.smallGap;
  const rulesTop = typeLineTop + v.bodySize + v.rulesGap;

  return (
    <div
      data-card-tile="true"
      style={cardStyle}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseEnter={() => onHover?.(card)}
      onMouseLeave={() => onHover?.(null)}
      {...interactiveProps}
    >
      <div
        style={{ position: "absolute", inset: 0, background: rarityWash, pointerEvents: "none" }}
      />
      <CardFoil rarity={card.rarity} />
      {dim && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "repeating-linear-gradient(45deg, rgba(26,15,5,0.18) 0 3px, transparent 3px 8px)",
            pointerEvents: "none",
            zIndex: 4,
          }}
        />
      )}
      <PixelCorner pos="tl" color={rb.color} />
      <PixelCorner pos="tr" color={rb.color} />
      <PixelCorner pos="bl" color={rb.color} />
      <PixelCorner pos="br" color={rb.color} />

      <div
        style={{
          position: "absolute",
          top: `${v.pad}cqw`,
          left: `${v.pad}cqw`,
          right: `${v.pad}cqw`,
          display: "flex",
          alignItems: "flex-start",
          gap: `${v.smallGap}cqw`,
          zIndex: 2,
        }}
      >
        <CostBadge
          cost={card.cost}
          gemCqw={v.costGem}
          fontCqw={v.costGemFont}
          borderPx={v.costGemBorderPx}
          theme={theme}
          factionColor={f.color}
          factionInk={f.ink}
        />
        <div style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
          <FitText
            text={card.name}
            maxSize={v.nameSize}
            maxLines={3}
            style={{
              fontFamily: theme.fontDisplay,
              fontWeight: 600,
              color: theme.ink,
              width: "100%",
            }}
          />
        </div>
        <Sigil
          faction={f.key}
          size={`${v.topRightSigilSize}cqw`}
          color={f.sigilInk}
          style={{ paddingTop: 2 }}
        />
      </div>

      <div
        style={{
          position: "absolute",
          top: `${artTop}cqw`,
          left: `${v.pad}cqw`,
          right: `${v.pad}cqw`,
          height: `${artLifect}cqw`,
          background: `repeating-linear-gradient(45deg, ${rb.color}22 0 6px, ${rb.color}11 6px 12px), radial-gradient(circle at 50% 40%, ${rb.color}44, transparent 70%)`,
          border: `1px solid ${rb.color}66`,
          borderRadius: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {artContent}
      </div>

      <div
        style={{
          position: "absolute",
          top: `${typeLineTop}cqw`,
          left: `${v.pad}cqw`,
          right: `${v.pad}cqw`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 4,
          fontFamily: theme.fontBody,
          fontSize: `${v.typeLineFont}cqw`,
          letterSpacing: theme.letterSpacing,
          textTransform: theme.uppercase ? "uppercase" : "none",
          fontVariant: theme.uppercase ? "normal" : "small-caps",
          color: theme.inkDim,
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          {formatTypeLine(card)}
        </span>
        {stats}
      </div>

      {v.showRulesText && card.rules_text && (
        <div
          style={{
            position: "absolute",
            top: `${rulesTop}cqw`,
            left: `${v.pad}cqw`,
            right: `${v.pad}cqw`,
            bottom: `${v.rulesBottomInset}cqw`,
            color: theme.ink,
            fontFamily: theme.fontBody,
            overflow: "hidden",
          }}
        >
          <FitBlock maxSize={v.bodySize} lineHeight={1.35}>
            {renderRules(card.rules_text, keywordTooltips ? { theme } : undefined)}
            {v.showFlavorText && card.flavor && (
              <div
                style={{
                  // Flavor renders in fontDisplay (Press Start 2P), whose
                  // glyphs are far chunkier per em than the rules' fontBody
                  // (VT323). At the same font size the flavor would dwarf the
                  // rules text and dominate the shared FitBlock's shrink loop,
                  // leaving the rules tiny. Knock it down so flavor reads as
                  // the secondary, smaller line it's meant to be.
                  fontSize: "0.62em",
                  fontStyle: "italic",
                  color: theme.inkFaint,
                  borderTop: `1px solid ${theme.panelEdge}`,
                  paddingTop: 6,
                  marginTop: 6,
                  fontFamily: theme.fontDisplay,
                }}
              >
                {card.flavor}
              </div>
            )}
          </FitBlock>
        </div>
      )}

      {v.showWorldFooter && card.expansion_set && (
        <div
          style={{
            position: "absolute",
            bottom: `${v.worldFooterBottom}cqw`,
            left: 0,
            right: 0,
            textAlign: "center",
            fontFamily: theme.fontBody,
            fontSize: `${v.worldFont}cqw`,
            letterSpacing: theme.letterSpacing,
            textTransform: theme.uppercase ? "uppercase" : "none",
            fontVariant: theme.uppercase ? "normal" : "small-caps",
            color: theme.inkFaint,
            opacity: 0.7,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 4,
          }}
        >
          <Sigil
            src={ORNAMENT_SIGIL}
            size={`${v.worldSigilSize}cqw`}
            color={theme.inkDim}
          />
          {worldLabel(card.expansion_set)}
        </div>
      )}

      {badge && (
        <div
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            padding: "2px 6px",
            background: theme.panelEdgeStrong,
            color: theme.panelSolid,
            fontFamily: theme.fontDisplay,
            fontSize: 9,
            border: `2px solid ${theme.bgDeep}`,
            zIndex: 5,
          }}
        >
          {badge}
        </div>
      )}
    </div>
  );
}

function CostBadge({
  cost,
  gemCqw,
  fontCqw,
  borderPx,
  theme,
  factionColor,
  factionInk,
}: {
  cost: number;
  gemCqw: number;
  fontCqw: number;
  borderPx: number;
  theme: Theme;
  factionColor: string;
  factionInk: string;
}) {
  const px = borderPx;
  return (
    <div
      style={{
        width: `${gemCqw}cqw`,
        height: `${gemCqw}cqw`,
        background: factionColor,
        border: `${px}px solid ${theme.panelEdgeStrong}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: theme.fontDisplay,
        fontSize: `${fontCqw}cqw`,
        color: factionInk,
        flexShrink: 0,
        boxShadow: `inset ${px}px ${px}px 0 rgba(255,255,255,0.35), inset -${px}px -${px}px 0 rgba(0,0,0,0.35)`,
        imageRendering: "pixelated",
      }}
    >
      {cost}
    </div>
  );
}

function PixelCorner({ pos, color }: { pos: "tl" | "tr" | "bl" | "br"; color: string }) {
  const positions: Record<typeof pos, CSSProperties> = {
    tl: { top: 0, left: 0 },
    tr: { top: 0, right: 0 },
    bl: { bottom: 0, left: 0 },
    br: { bottom: 0, right: 0 },
  };
  return (
    <div
      style={{
        position: "absolute",
        ...positions[pos],
        width: 4,
        height: 4,
        background: color,
        pointerEvents: "none",
        zIndex: 3,
      }}
    />
  );
}
